import React, { useState, useTransition } from "react";
import { Dropdown, Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { GrFlagFill } from "react-icons/gr";
import { PermissionedButton, usePermissionCheck } from "@components/PermissionedComponents";
import { useStatusTrackerV2 } from "../hooks/useStatusTrackerV2";
import { BulkTagModal } from "./BulkTagModal";

// Same permission that gates creating a user defined flag in Settings.
const FLAG_PERMISSION = "ManageCustomFlagConfigurations";

const V2_DISABLED_MESSAGE = "Status Tracker V2 is not enabled for this team yet";

// Says what a coach flag is, not what the button does — someone meeting the grid for the first
// time has no reason to know its flags are usually computed.
const HELPER_TEXT =
    "Regular flags are raised automatically: a rule runs over each player's data overnight " +
    "and flags anyone it matches. A coach flag is one you add yourself instead — pick a " +
    "player, a date, and an optional note, to record something you saw that the data " +
    "wouldn't show.";

const SINGLE = "single";
const BULK = "bulk";

// Flag entry point for the filter bar. Single Player toggles the grid into tagging mode, where a
// cell click tags that one cell; Bulk opens a dialog to tag many cells at once.
export const AddFlagButton = ({ tooltipPlacement = "top" }) => {
    const { permissionDisabled } = usePermissionCheck(FLAG_PERMISSION);
    const { state, actions } = useStatusTrackerV2();
    const [bulkOpen, setBulkOpen] = useState(false);
    // Switching mode re-renders every cell in the grid. Marking it a transition keeps the
    // click responsive and gives `pending` to show on the button while React works.
    const [pending, startTransition] = useTransition();

    // Stays disabled until the month view confirms the team is on V2, so it never offers an
    // action the backend would reject.
    const v2Disabled = state.v2Enabled !== true;
    // PermissionedButton owns the tooltip for the permission case. Otherwise say why the button
    // is unusable, or — when it is usable — what a coach flag is.
    const tooltip = permissionDisabled ? "" : v2Disabled ? V2_DISABLED_MESSAGE : HELPER_TEXT;

    const singleOn = state.taggingMode === SINGLE;
    const menuItems = [
        {
            key: SINGLE,
            label: singleOn ? "Single Player — on" : "Single Player",
            icon: singleOn ? <CheckOutlined /> : null,
        },
        { key: BULK, label: "Bulk" },
    ];

    return (
        <>
        <Dropdown
            menu={{
                items: menuItems,
                selectable: true,
                selectedKeys: singleOn ? [SINGLE] : [],
                onClick: ({ key }) => {
                    // Picking Single Player again leaves tagging mode, so the menu is the
                    // toggle as well as the way in.
                    if (key === SINGLE) {
                        startTransition(() => actions.setTaggingMode(singleOn ? null : SINGLE));
                    }
                    if (key === BULK) setBulkOpen(true);
                },
            }}
            placement="bottomRight"
            disabled={permissionDisabled || v2Disabled}
        >
            {/* Two sentences wrap to a narrow column at antd's default 250px. */}
            <Tooltip title={tooltip} placement={tooltipPlacement} overlayStyle={{ maxWidth: 380 }}>
                <PermissionedButton
                    permissionCheck={FLAG_PERMISSION}
                    permissionMessage="You don't have permission to manage flags"
                    type="primary"
                    loading={pending}
                    disabled={v2Disabled}
                    className={`stv2-add-flag-button${singleOn ? " stv2-add-flag-button--on" : ""}`}
                    aria-pressed={singleOn}
                >
                    {/* Label first, then the flag — as children rather than `icon`, which
                        antd would place ahead of the text. */}
                    + Coach Flags <GrFlagFill />
                </PermissionedButton>
            </Tooltip>
        </Dropdown>
        <BulkTagModal open={bulkOpen} onClose={() => setBulkOpen(false)} />
        </>
    );
};
